import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Alert,
  IconButton,
  Snackbar,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { palette } from '../constants/palette';

interface LocationState {
  companyId?: string;
  companyName?: string;
  mailAddress?: string;
  temporaryPassword?: string;
}

const SimpleFormSuccess: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) || null;

  const companyId = state?.companyId;
  const companyName = state?.companyName;
  const mailAddress = state?.mailAddress;
  const temporaryPassword = state?.temporaryPassword;

  const [copiedSnackbarOpen, setCopiedSnackbarOpen] = useState(false);
  const [copiedField, setCopiedField] = useState('');

  const handleCopy = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setCopiedSnackbarOpen(true);
    } catch (error) {
      console.error('Error copying text:', error);
    }
  };

  const handleCloseSnackbar = () => {
    setCopiedSnackbarOpen(false);
  };

  if (!companyId) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mt: 2,
            textAlign: 'center',
            borderRadius: 3,
            border: `1px solid ${palette.primaryLighter}`,
          }}
        >
          <Typography variant="h5" gutterBottom>
            {t('simpleFormSuccess.notFound.title')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('simpleFormSuccess.notFound.description')}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/simple-form')}>
            {t('simpleFormSuccess.notFound.backButton')}
          </Button>
        </Paper>
      </Container>
    );
  }

  const qrValue = companyId;

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mt: 2,
          textAlign: 'center',
          borderRadius: 3,
          border: `1px solid ${palette.primaryLighter}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
          <Typography variant="h4" gutterBottom>
            {t('simpleFormSuccess.success.title')}
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ mb: 3 }}>
          {t('simpleFormSuccess.success.description')}
        </Typography>

        {temporaryPassword && (
          <Box sx={{ mt: 3, mb: 3 }}>
            <Alert
              severity="warning"
              icon={false}
              sx={{
                textAlign: 'left',
                '& .MuiAlert-message': {
                  width: '100%',
                  p: 0,
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                }}
              >
                <WarningAmberOutlinedIcon fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {t('simpleFormSuccess.temporaryPassword.title')}
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ mb: 2 }}>
                {t('simpleFormSuccess.temporaryPassword.description')}
              </Typography>

              <Box
                sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  p: 2,
                  borderRadius: 1,
                  width: { xs: '100%', sm: '85%' },
                  mx: 'auto',
                  textAlign: 'center',
                }}
              >
                {mailAddress && (
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 0.5,
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {t('simpleFormSuccess.temporaryPassword.emailLabel')}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleCopy(mailAddress, t('simpleFormSuccess.snackbar.email'))
                        }
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        backgroundColor: 'white',
                        p: 1,
                        borderRadius: 1,
                        wordBreak: 'break-all',
                        textAlign: 'center',
                      }}
                    >
                      {mailAddress}
                    </Typography>
                  </Box>
                )}

                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 0.5,
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {t('simpleFormSuccess.temporaryPassword.passwordLabel')}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleCopy(
                          temporaryPassword,
                          t('simpleFormSuccess.snackbar.temporaryPassword'),
                        )
                      }
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      backgroundColor: 'white',
                      p: 1,
                      borderRadius: 1,
                      wordBreak: 'break-all',
                      textAlign: 'center',
                    }}
                  >
                    {temporaryPassword}
                  </Typography>
                </Box>
              </Box>
            </Alert>
          </Box>
        )}

        {companyName && (
          <Typography variant="h6" sx={{ mb: 1 }}>
            {companyName}
          </Typography>
        )}

        {mailAddress && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            {mailAddress}
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <Box
            sx={{
              p: 2,
              backgroundColor: 'white',
              borderRadius: 2,
              border: `1px solid ${palette.primaryLighter}`,
            }}
          >
            <QRCodeCanvas value={qrValue} size={220} />
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={copiedSnackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={t('simpleFormSuccess.snackbar.copied', { field: copiedField })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};

export default SimpleFormSuccess;
